import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Save, Upload, Trash2, Plus, ExternalLink, Inbox } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useContent } from "../context/ContentContext";
import { api, mediaUrl } from "../lib/api";
import Logo from "../components/Logo";

const TABS = ["general", "services", "gallery", "reviews", "leads"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { content, load, save } = useContent();
  const nav = useNavigate();
  const [tab, setTab] = useState("general");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    if (user === false) nav("/admin/login");
  }, [user, nav]);

  useEffect(() => {
    if (content) setDraft(JSON.parse(JSON.stringify(content)));
  }, [content]);

  useEffect(() => {
    if (user && user !== false) api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
  }, [user]);

  if (user === null || !draft) {
    return <div className="min-h-screen flex items-center justify-center text-silver font-mono">Loading...</div>;
  }
  if (user === false) return null;

  const saveAll = async () => {
    setSaving(true);
    try {
      await save(draft);
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doLogout = async () => { await logout(); nav("/admin/login"); };

  const upd = (path, value) => {
    setDraft((d) => {
      const next = JSON.parse(JSON.stringify(d));
      let obj = next;
      for (let i = 0; i < path.length - 1; i++) obj = obj[path[i]];
      obj[path[path.length - 1]] = value;
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-obsidian grain">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-obsidian/90 backdrop-blur-xl">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={40} />
            <span className="font-display uppercase text-white text-lg hidden sm:block">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 border border-white/15 px-3 py-2 text-xs font-mono uppercase text-silver hover:text-acid hover:border-acid transition-colors" data-testid="view-site">
              <ExternalLink size={14} /> Site
            </a>
            <button onClick={saveAll} disabled={saving} className="flex items-center gap-2 bg-acid px-4 py-2 text-xs font-heading font-semibold uppercase text-obsidian hover:bg-acid-hover transition-colors disabled:opacity-60" data-testid="save-all">
              <Save size={14} /> {saving ? "Saving..." : "Save Changes"}
            </button>
            <button onClick={doLogout} className="flex items-center gap-2 border border-white/15 px-3 py-2 text-xs font-mono uppercase text-silver hover:text-red-400 hover:border-red-400 transition-colors" data-testid="logout-btn">
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-px bg-white/10 border border-white/10 mb-8 w-fit">
          {TABS.map((tb) => (
            <button
              key={tb}
              onClick={() => setTab(tb)}
              className={`px-6 py-3 font-mono text-xs uppercase tracking-widest transition-colors ${tab === tb ? "bg-acid text-obsidian" : "bg-obsidian text-silver hover:text-white"}`}
              data-testid={`tab-${tb}`}
            >
              {tb} {tb === "leads" && leads.length > 0 && `(${leads.length})`}
            </button>
          ))}
        </div>

        {tab === "general" && <General draft={draft} upd={upd} />}
        {tab === "services" && <Services draft={draft} setDraft={setDraft} />}
        {tab === "gallery" && <Gallery draft={draft} setDraft={setDraft} />}
        {tab === "reviews" && <Reviews draft={draft} setDraft={setDraft} />}
        {tab === "leads" && <Leads leads={leads} setLeads={setLeads} />}
      </div>
    </div>
  );
}

/* ---------- shared fields ---------- */
const inputCls = "w-full bg-obsidian border border-white/15 px-3 py-2.5 text-white placeholder:text-white/25 outline-none focus:border-acid transition-colors text-sm";

function Row({ label, children }) {
  return (
    <label className="block mb-4">
      <span className="font-mono text-[10px] uppercase tracking-widest text-silver mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

function Card({ title, children }) {
  return (
    <div className="border border-white/10 bg-surface p-6 mb-6">
      <h3 className="font-heading uppercase text-xl text-white mb-5 border-b border-white/10 pb-3">{title}</h3>
      {children}
    </div>
  );
}

function BilingualPair({ draft, upd, base, field, label, textarea }) {
  const Comp = textarea ? "textarea" : "input";
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <Row label={`${label} (EN)`}>
        <Comp value={draft[base][`${field}_en`] || ""} onChange={(e) => upd([base, `${field}_en`], e.target.value)} className={inputCls} rows={textarea ? 3 : undefined} data-testid={`${base}-${field}-en`} />
      </Row>
      <Row label={`${label} (ES)`}>
        <Comp value={draft[base][`${field}_es`] || ""} onChange={(e) => upd([base, `${field}_es`], e.target.value)} className={inputCls} rows={textarea ? 3 : undefined} data-testid={`${base}-${field}-es`} />
      </Row>
    </div>
  );
}

/* ---------- General ---------- */
function General({ draft, upd }) {
  return (
    <div className="max-w-4xl">
      <Card title="Hero Section">
        <BilingualPair draft={draft} upd={upd} base="hero" field="kicker" label="Kicker" />
        <BilingualPair draft={draft} upd={upd} base="hero" field="title" label="Title (use line breaks)" textarea />
        <BilingualPair draft={draft} upd={upd} base="hero" field="subtitle" label="Subtitle" textarea />
      </Card>
      <Card title="About Section">
        <BilingualPair draft={draft} upd={upd} base="about" field="heading" label="Heading" />
        <BilingualPair draft={draft} upd={upd} base="about" field="body" label="Body" textarea />
      </Card>
      <Card title="Contact Info">
        <div className="grid sm:grid-cols-2 gap-4">
          <Row label="Phone"><input value={draft.contact.phone || ""} onChange={(e) => upd(["contact", "phone"], e.target.value)} className={inputCls} data-testid="contact-phone" /></Row>
          <Row label="Email"><input value={draft.contact.email || ""} onChange={(e) => upd(["contact", "email"], e.target.value)} className={inputCls} data-testid="contact-email" /></Row>
          <Row label="Facebook URL"><input value={draft.contact.facebook || ""} onChange={(e) => upd(["contact", "facebook"], e.target.value)} className={inputCls} /></Row>
          <Row label="Instagram URL"><input value={draft.contact.instagram || ""} onChange={(e) => upd(["contact", "instagram"], e.target.value)} className={inputCls} /></Row>
        </div>
        <Row label="Google Maps location / search (for embedded map)">
          <input value={draft.contact.map_query || ""} onChange={(e) => upd(["contact", "map_query"], e.target.value)} className={inputCls} placeholder="Winchester, VA" data-testid="contact-map-query" />
        </Row>
        <BilingualPair draft={draft} upd={upd} base="contact" field="area" label="Service Area" />
        <BilingualPair draft={draft} upd={upd} base="contact" field="hours" label="Hours" />
      </Card>
    </div>
  );
}

/* ---------- Services ---------- */
function Services({ draft, setDraft }) {
  const services = draft.services || [];
  const set = (i, key, val) => {
    setDraft((d) => {
      const next = JSON.parse(JSON.stringify(d));
      next.services[i][key] = val;
      return next;
    });
  };
  const uploadImg = async (i, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(i, "image", data.url);
      toast.success("Image uploaded (remember to Save)");
    } catch { toast.error("Upload failed"); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-5 max-w-5xl">
      {services.map((s, i) => (
        <div key={s.id || i} className="border border-white/10 bg-surface p-5" data-testid={`admin-service-${i}`}>
          <div className="flex items-center gap-3 mb-4">
            <span className="font-mono text-acid">0{i + 1}</span>
            <div className="h-14 w-14 border border-white/10 bg-obsidian overflow-hidden shrink-0">
              {s.image && <img src={mediaUrl(s.image)} alt="" className="h-full w-full object-cover" />}
            </div>
            <label className="flex items-center gap-2 border border-white/15 px-3 py-2 text-xs font-mono uppercase text-silver hover:text-acid hover:border-acid transition-colors cursor-pointer">
              <Upload size={14} /> Image
              <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImg(i, e.target.files[0])} data-testid={`service-upload-${i}`} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input value={s.title_en} onChange={(e) => set(i, "title_en", e.target.value)} className={inputCls} placeholder="Title EN" />
            <input value={s.title_es} onChange={(e) => set(i, "title_es", e.target.value)} className={inputCls} placeholder="Title ES" />
            <textarea value={s.desc_en} onChange={(e) => set(i, "desc_en", e.target.value)} className={inputCls} rows={2} placeholder="Desc EN" />
            <textarea value={s.desc_es} onChange={(e) => set(i, "desc_es", e.target.value)} className={inputCls} rows={2} placeholder="Desc ES" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Gallery ---------- */
function Gallery({ draft, setDraft }) {
  const fileRef = useRef();
  const gallery = draft.gallery || [];
  const [uploading, setUploading] = useState(false);

  const add = async (files) => {
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setDraft((d) => {
          const next = JSON.parse(JSON.stringify(d));
          next.gallery = next.gallery || [];
          next.gallery.push({ id: crypto.randomUUID(), url: data.url, caption_en: "", caption_es: "" });
          return next;
        });
      }
      toast.success("Uploaded — click Save Changes to publish");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const remove = (i) => setDraft((d) => {
    const next = JSON.parse(JSON.stringify(d));
    next.gallery.splice(i, 1);
    return next;
  });

  const setCap = (i, key, val) => setDraft((d) => {
    const next = JSON.parse(JSON.stringify(d));
    next.gallery[i][key] = val;
    return next;
  });

  return (
    <div className="max-w-5xl">
      <div className="border border-dashed border-white/20 p-8 text-center mb-8">
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => e.target.files.length && add([...e.target.files])} data-testid="gallery-file-input" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 bg-acid px-6 py-3 font-heading font-semibold uppercase text-obsidian hover:bg-acid-hover transition-colors disabled:opacity-60" data-testid="gallery-upload-btn">
          <Plus size={18} /> {uploading ? "Uploading..." : "Add Images"}
        </button>
        <p className="text-silver text-xs font-mono mt-3">Upload work photos. Click Save Changes to publish.</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {gallery.map((g, i) => (
          <div key={g.id || i} className="border border-white/10 bg-surface" data-testid={`admin-gallery-${i}`}>
            <div className="relative h-40 bg-obsidian">
              <img src={mediaUrl(g.url)} alt="" className="h-full w-full object-cover" />
              <button onClick={() => remove(i)} className="absolute top-2 right-2 bg-red-500/90 p-2 text-white hover:bg-red-500 transition-colors" data-testid={`gallery-remove-${i}`}>
                <Trash2 size={14} />
              </button>
            </div>
            <div className="p-3 space-y-2">
              <input value={g.caption_en || ""} onChange={(e) => setCap(i, "caption_en", e.target.value)} className={inputCls} placeholder="Caption EN" />
              <input value={g.caption_es || ""} onChange={(e) => setCap(i, "caption_es", e.target.value)} className={inputCls} placeholder="Caption ES" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Reviews ---------- */
function Reviews({ draft, setDraft }) {
  const reviews = draft.reviews || [];
  const setUrl = (val) => setDraft((d) => ({ ...JSON.parse(JSON.stringify(d)), google_url: val }));
  const set = (i, key, val) => setDraft((d) => {
    const next = JSON.parse(JSON.stringify(d));
    next.reviews[i][key] = val;
    return next;
  });
  const add = () => setDraft((d) => {
    const next = JSON.parse(JSON.stringify(d));
    next.reviews = next.reviews || [];
    next.reviews.push({ id: crypto.randomUUID(), author: "", rating: 5, text: "", location: "" });
    return next;
  });
  const remove = (i) => setDraft((d) => {
    const next = JSON.parse(JSON.stringify(d));
    next.reviews.splice(i, 1);
    return next;
  });

  return (
    <div className="max-w-4xl">
      <Card title="Google Profile Link">
        <Row label="Google reviews / business URL (button links here)">
          <input value={draft.google_url || ""} onChange={(e) => setUrl(e.target.value)} className={inputCls} placeholder="https://g.page/r/..." data-testid="google-url" />
        </Row>
      </Card>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-heading uppercase text-xl text-white">Reviews ({reviews.length})</h3>
        <button onClick={add} className="flex items-center gap-2 bg-acid px-4 py-2 text-xs font-heading font-semibold uppercase text-obsidian hover:bg-acid-hover transition-colors" data-testid="add-review">
          <Plus size={14} /> Add Review
        </button>
      </div>
      <div className="space-y-4">
        {reviews.map((r, i) => (
          <div key={r.id || i} className="border border-white/10 bg-surface p-5" data-testid={`admin-review-${i}`}>
            <div className="grid sm:grid-cols-3 gap-3 mb-3">
              <input value={r.author} onChange={(e) => set(i, "author", e.target.value)} className={inputCls} placeholder="Author name" />
              <input value={r.location || ""} onChange={(e) => set(i, "location", e.target.value)} className={inputCls} placeholder="Location (optional)" />
              <select value={r.rating} onChange={(e) => set(i, "rating", Number(e.target.value))} className={inputCls}>
                {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} ★</option>)}
              </select>
            </div>
            <textarea value={r.text} onChange={(e) => set(i, "text", e.target.value)} className={inputCls} rows={3} placeholder="Review text" />
            <div className="flex justify-end mt-3">
              <button onClick={() => remove(i)} className="flex items-center gap-2 border border-white/15 px-3 py-2 text-xs font-mono uppercase text-silver hover:text-red-400 hover:border-red-400 transition-colors" data-testid={`review-remove-${i}`}>
                <Trash2 size={14} /> Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Leads ---------- */
function Leads({ leads, setLeads }) {
  const del = async (id) => {
    try { await api.delete(`/leads/${id}`); setLeads((l) => l.filter((x) => x.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };
  if (leads.length === 0) {
    return (
      <div className="border border-white/10 bg-surface py-20 text-center text-silver">
        <Inbox className="mx-auto mb-4 text-white/20" size={40} />
        <p className="font-mono uppercase text-sm tracking-widest">No estimate requests yet</p>
      </div>
    );
  }
  return (
    <div className="space-y-3 max-w-4xl">
      {leads.map((l) => (
        <div key={l.id} className="border border-white/10 bg-surface p-5 flex justify-between gap-4" data-testid={`lead-${l.id}`}>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-heading text-lg text-white">{l.name}</span>
              <a href={`tel:${(l.phone || "").replace(/[^0-9]/g, "")}`} className="text-acid font-mono text-sm">{l.phone}</a>
              {l.email && <span className="text-silver text-sm">{l.email}</span>}
            </div>
            {l.service && <p className="font-mono text-xs uppercase tracking-widest text-acid mt-1">{l.service}</p>}
            {l.message && <p className="text-silver text-sm mt-2">{l.message}</p>}
            <p className="text-white/30 text-xs font-mono mt-2">{new Date(l.created_at).toLocaleString()}</p>
          </div>
          <button onClick={() => del(l.id)} className="self-start border border-white/15 p-2 text-silver hover:text-red-400 hover:border-red-400 transition-colors" data-testid={`lead-delete-${l.id}`}>
            <Trash2 size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}
